import { useRef, useCallback, useState, useEffect } from "react";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useRandomHexColor } from "../hooks/useRandomHexColor";
import {
  CustomMarkersContext,
  type CustomMarker,
  type CustomMarkerGroup,
} from "../context/CustomMarkersContext";
import { MARKERS_COOKIE_KEY } from "../utils/constants";

type PersistedMarker = Omit<CustomMarker, "date"> & { date: string | null };
type PersistedGroup = Omit<CustomMarkerGroup, "markers"> & {
  markers: PersistedMarker[];
};

function loadFromCookie(): {
  groups: CustomMarkerGroup[];
  maxGroupId: number;
} | null {
  try {
    const str = Cookies.get(MARKERS_COOKIE_KEY);
    if (!str) return null;
    const raw: PersistedGroup[] = JSON.parse(str);
    if (!Array.isArray(raw)) return null;
    let maxGroupId = 0;
    const groups: CustomMarkerGroup[] = raw.map((g) => {
      const gid = Number(g.id);
      if (gid >= maxGroupId) maxGroupId = gid + 1;
      return {
        id: gid,
        name: typeof g.name === "string" ? g.name : "Marker group",
        color: typeof g.color === "string" ? g.color : "#1677ff",
        markers: Array.isArray(g.markers)
          ? g.markers.map((m) => ({
              // support old numeric IDs by converting to string
              id:
                typeof m.id === "string"
                  ? m.id
                  : String(m.id ?? crypto.randomUUID()),
              name: typeof m.name === "string" ? m.name : "",
              date: typeof m.date === "string" && m.date ? dayjs(m.date) : null,
              latitude: typeof m.latitude === "string" ? m.latitude : "",
              longitude: typeof m.longitude === "string" ? m.longitude : "",
              description:
                typeof m.description === "string" ? m.description : "",
            }))
          : [],
      };
    });
    return { groups, maxGroupId };
  } catch {
    return null;
  }
}

function saveToCookie(groups: CustomMarkerGroup[]) {
  try {
    const data: PersistedGroup[] = groups.map((g) => ({
      ...g,
      markers: g.markers.map((m) => ({
        ...m,
        date: m.date ? m.date.toISOString() : null,
      })),
    }));
    Cookies.set(MARKERS_COOKIE_KEY, JSON.stringify(data), {
      expires: 365,
      sameSite: "Lax",
      path: "/",
    });
  } catch {
    // ignore
  }
}

export function CustomMarkersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const randomColor = useRandomHexColor();
  const groupIdRef = useRef(0);

  const [groups, setGroups] = useState<CustomMarkerGroup[]>(() => {
    const saved = loadFromCookie();
    console.log("Loaded custom marker groups from cookie:", saved);
    if (saved) {
      groupIdRef.current = saved.maxGroupId;
      return saved.groups;
    }
    groupIdRef.current = 1;
    return [
      {
        id: 0,
        name: "D. B. Cooper",
        color: "#a0a4a6",
        markers: [
          {
            id: crypto.randomUUID(),
            name: "The Hijacking",
            date: dayjs("1971-11-24"),
            latitude: "45.5898",
            longitude: "-122.5951",
            description:
              'A man using the name <a href="https://en.wikipedia.org/wiki/D._B._Cooper" target="_blank" rel="noreferrer noopener">D. B. Cooper</a> boards Northwest Orient Airlines Flight 305 to Seattle.Shortly after takeoff, he hands a note to a flight attendant claiming he has a bomb.',
          },
          {
            id: crypto.randomUUID(),
            name: "Landing in Seattle",
            date: dayjs("1971-11-24"),
            latitude: "47.4502",
            longitude: "-122.3088",
            description:
              "Passengers are released. Cooper receives the ransom and parachutes. Orders the plane to take off again toward Mexico.",
          },
          {
            id: crypto.randomUUID(),
            name: "The Jump",
            date: dayjs("1971-11-24"),
            latitude: "45.9990",
            longitude: "-122.4800",
            description:
              "Cooper lowers the rear stairs of the Boeing 727 mid-flight. Jumps out with the money into the night during bad weather. He is never seen again.",
          },
          {
            id: crypto.randomUUID(),
            name: "Money Found",
            date: dayjs("1980-02-10"),
            latitude: "45.7617",
            longitude: "-122.2500",
            description:
              "A young boy discovers $5,800 in decaying ransom money buried in sand. Serial numbers match the ransom.",
          },
        ],
      },
    ];
  });

  useEffect(() => {
    saveToCookie(groups);
  }, [groups]);

  const addGroup = useCallback(() => {
    setGroups((prev) => [
      ...prev,
      {
        id: groupIdRef.current++,
        color: randomColor(),
        name: `Marker group ${prev.length + 1}`,
        markers: [
          {
            id: crypto.randomUUID(),
            name: "Marker 1",
            date: dayjs(),
            latitude: "",
            longitude: "",
            description: "",
          },
        ],
      },
    ]);
  }, [randomColor]);

  const removeGroup = useCallback((groupId: number) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const updateGroupName = useCallback((groupId: number, name: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, name } : g)),
    );
  }, []);

  const updateGroupColor = useCallback((groupId: number, color: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, color } : g)),
    );
  }, []);

  const addMarker = useCallback((groupId: number) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          markers: [
            ...g.markers,
            {
              id: crypto.randomUUID(),
              name: `Marker ${g.markers.length + 1}`,
              date: dayjs(),
              latitude: "",
              longitude: "",
              description: "",
            },
          ],
        };
      }),
    );
  }, []);

  const removeMarker = useCallback((groupId: number, markerId: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        return { ...g, markers: g.markers.filter((m) => m.id !== markerId) };
      }),
    );
  }, []);

  const updateMarker = useCallback(
    <K extends keyof CustomMarker>(
      groupId: number,
      markerId: string,
      key: K,
      value: CustomMarker[K],
    ) => {
      setGroups((prev) =>
        prev.map((g) => {
          if (g.id !== groupId) return g;
          return {
            ...g,
            markers: g.markers.map((m) =>
              m.id === markerId ? { ...m, [key]: value } : m,
            ),
          };
        }),
      );
    },
    [],
  );

  const setGroupVisible = useCallback((groupId: number, visible: boolean) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, visible } : g)),
    );
  }, []);

  const importGroups = useCallback((incoming: CustomMarkerGroup[]) => {
    let maxGroupId = 0;
    for (const g of incoming) {
      if (g.id >= maxGroupId) maxGroupId = g.id + 1;
    }
    groupIdRef.current = maxGroupId;
    setGroups(incoming);
  }, []);

  const clearGroups = useCallback(() => {
    groupIdRef.current = 0;
    setGroups([]);
  }, []);

  return (
    <CustomMarkersContext.Provider
      value={{
        groups,
        addGroup,
        removeGroup,
        updateGroupName,
        updateGroupColor,
        setGroupVisible,
        addMarker,
        removeMarker,
        updateMarker,
        importGroups,
        clearGroups,
      }}
    >
      {children}
    </CustomMarkersContext.Provider>
  );
}
